import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import AppointmentCounts from "../components/AppointmentCounts";
import AppointmentFilter from "../components/AppointmentFilter";
import type { DoctorOption } from "../components/AppointmentFilter";
import AppointmentTr from "../components/AppointmentTr";
import AppointmentDetailsModal from "../components/AppointmentDetailsModal";
import AppointmentTableHead from "../components/AppointmentTableHead";
import AppointmentMobileCard from "../components/AppointmentMobileCard";
import baseUrl from "../../../../api/baseUrl";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import { RootState } from "../../../../redux/store";
import {
  setAllAppointment,
  setAllAppointmentCount,
} from "../../../../redux/appointment/appointmentAction";
import { setDoctor } from "../../../../redux/doctor/doctorAction";
import BottomTab from "../../../../components/pagination/BottomTab";
import { resolveListStatusHeader } from "../components/appointmentViewShared";
import socket from "../../../../api/socket";
import getDecryptedToken from "../../../../api/getDecryptedToken";

/** Count chip / card keys → API status header values. */
const COUNT_TO_STATUS: Record<string, string> = {
  all: "pending",
  schedule: "excl_cancel",
  upcoming: "upcoming",
  in_queue: "in_queue",
  ongoing: "ongoing",
  complete: "complete",
  no_show: "no_show",
  rescheduled: "rescheduled",
  tray: "rescheduled",
  appointment: "appointment",
  walk_in: "walk_in",
  call_in: "call_in",
  online: "online",
  cancel: "cancel",
  pending: "pending",
  excl_cancel: "excl_cancel",
};

const STATUS_TO_COUNT: Record<string, string> = {
  pending: "all",
  all: "all",
  schedule: "schedule",
  excl_cancel: "schedule",
  all_exc_cancel: "schedule",
  upcoming: "upcoming",
  in_queue: "in_queue",
  inqueue: "in_queue",
  ongoing: "ongoing",
  complete: "complete",
  completed: "complete",
  no_show: "no_show",
  noshow: "no_show",
  rescheduled: "rescheduled",
  tray: "rescheduled",
  appointment: "appointment",
  walk_in: "walk_in",
  walkin: "walk_in",
  call_in: "call_in",
  callin: "call_in",
  online: "online",
  cancel: "cancel",
  cancelled: "cancel",
  canceled: "cancel",
};

function mergeSessionNames(...lists: (string[] | undefined)[]): string[] {
  const set = new Set<string>();
  for (const list of lists) {
    for (const name of list ?? []) {
      const trimmed = String(name).trim();
      if (trimmed) set.add(trimmed);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function buildAppointmentParams(
  all: string,
  session: string,
  filterDate: string,
  patientSearch: string
): Record<string, string> | undefined {
  const params: Record<string, string> = {};
  if (session.trim()) params.sessionName = session.trim();
  if (all === "all" && filterDate.trim()) params.filterDate = filterDate.trim();
  if (patientSearch.trim()) params.patientSearch = patientSearch.trim();
  return Object.keys(params).length ? params : undefined;
}

function normalizeCountsPayload(data: any) {
  return {
    ...data,
    by_view: data?.by_view,
    by_status: data?.by_status,
    by_source: data?.by_source,
    appointments: data?.appointments ?? data?.total,
    cancelled: data?.cancelled ?? data?.canceled,
    completed: data?.completed ?? data?.complete,
    complete: data?.complete ?? data?.completed,
    pending: data?.pending ?? data?.upcoming,
    inqueue: data?.inqueue ?? data?.in_queue,
    in_queue: data?.in_queue ?? data?.inqueue,
    waiting: data?.waiting,
    reschudle: data?.reschudle,
    rescheduled:
      data?.by_status?.rescheduled ?? data?.rescheduled ?? data?.tray,
    tray: data?.tray ?? data?.rescheduled,
    ongoing: data?.ongoing,
    noshow: data?.noshow ?? data?.no_show,
    no_show: data?.no_show ?? data?.noshow,
    walkin: data?.walkin ?? data?.walk_in,
    walk_in: data?.walk_in ?? data?.walkin,
    callin: data?.callin ?? data?.call_in,
    call_in: data?.call_in ?? data?.callin,
    assigned: data?.assigned,
    upcoming: data?.upcoming ?? data?.pending,
    appointment:
      data?.by_source?.appointment ??
      data?.appointment ??
      data?.pre_booked ??
      data?.schedule,
    schedule: data?.schedule ?? data?.pre_booked ?? data?.appointment,
    pre_booked: data?.pre_booked ?? data?.appointment ?? data?.schedule,
    online: data?.online,
    cancel:
      data?.by_source?.cancel ??
      data?.cancel ??
      data?.cancelled ??
      data?.canceled,
  };
}

const FILTER_LABELS: Record<string, string> = {
  pending: "All",
  all: "All",
  schedule: "Schedule",
  excl_cancel: "Schedule",
  complete: "Complete",
  upcoming: "Upcoming",
  in_queue: "In Queue",
  ongoing: "Ongoing",
  no_show: "No-Show",
  rescheduled: "Rescheduled",
  tray: "Rescheduled",
  appointment: "Appointment",
  walk_in: "Walk-in",
  call_in: "Call-in",
  online: "Online",
  cancel: "Cancel",
};

const AllAppointment: React.FC = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [detailsItem, setDetailsItem] = useState<any | null>(null);
  const [doctorid, setDoctorid] = useState<any>("");
  const [all, setAll] = useState<string>("today");
  const [selectedStatusCard, setSelectedStatusCard] = useState<string>("");
  const [selectedSourceChip, setSelectedSourceChip] = useState<string>("schedule");
  const [selectedSession, setSelectedSession] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [patientSearch, setPatientSearch] = useState("");
  const [debouncedPatientSearch, setDebouncedPatientSearch] = useState("");
  const [sessions, setSessions] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const dispatch = useDispatch();
  const appointment = useSelector((state: RootState) => state.appointment);
  const doctor = useSelector((state: RootState) => state.doctor);

  const selectedOption = useMemo(
    () =>
      resolveListStatusHeader({
        statusCard: selectedStatusCard,
        sourceChip: selectedSourceChip,
      }),
    [selectedStatusCard, selectedSourceChip]
  );

  const fetchAppointments = useCallback(
    async (
      status: string,
      all: string,
      doctorid: string,
      session: string,
      filterDate: string,
      patientSearchTerm: string,
      newPage: number
    ) => {
      setIsLoading(true);
      try {
        const response = await baseUrl.get("/appointment/findAllAppointments", {
          headers: { status, day: all, doctorid: doctorid, page: newPage },
          params: buildAppointmentParams(
            all,
            session,
            filterDate,
            patientSearchTerm
          ),
        });

        const rows = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        setCounts(response.data?.count ?? 0);
        dispatch(setAllAppointment(rows, false));
        setSessions((prev) =>
          mergeSessionNames(
            prev,
            rows.map((row: { sessionName?: string }) => row?.sessionName)
          )
        );
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );

  const loadSessionNames = useCallback(
    (day: string, doctorId: string, filterDate: string) => {
      baseUrl
        .get("/appointment/allAppointmentSessionNames", {
          headers: { day, doctorid: doctorId },
          params:
            day === "all" && filterDate.trim()
              ? { filterDate: filterDate.trim() }
              : undefined,
        })
      .then((response) => {
        const names = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        setSessions((prev) => mergeSessionNames(prev, names));
      })
      .catch((error) => {
        console.error(error);
      });
    },
    []
  );

  const fetchCounts = useCallback(
    async (day: string, doctorId: string, filterDate: string, session: string) => {
      try {
        const response = await baseUrl.get("/appointment/appointmentscounts", {
          headers: {
            status: true,
            day,
            doctorid: doctorId,
            filterDate: day === "all" && filterDate.trim() ? filterDate.trim() : "",
          },
          params: session.trim()
            ? { sessionName: session.trim() }
            : undefined,
        });

        dispatch(setAllAppointmentCount(normalizeCountsPayload(response.data)));
      } catch (error) {
        console.error(error);
      }
    },
    [dispatch]
  );

  const fetchDoctors = useCallback(async () => {
    try {
      const response = await baseUrl.get("/employee/findEmployee", {
        headers: { status: true },
      });
      dispatch(setDoctor({ data: response.data.data, empty: false }));
    } catch (error) {
      console.error(error);
    }
  }, [dispatch]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedPatientSearch(patientSearch.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [patientSearch]);

  useEffect(() => {
    loadSessionNames(all, doctorid, selectedDate);
  }, [all, doctorid, selectedDate, loadSessionNames]);

  useEffect(() => {
    if (appointment?.allappointmentIsEMpty)
      fetchAppointments(
        selectedOption,
        all,
        doctorid,
        selectedSession,
        selectedDate,
        debouncedPatientSearch,
        1
      );
    if (doctor.empty) fetchDoctors();
  }, [
    fetchAppointments,
    fetchDoctors,
    selectedOption,
    all,
    doctorid,
    selectedSession,
    selectedDate,
    appointment?.allappointmentIsEMpty,
    doctor.empty,
  ]);

  useEffect(() => {
    fetchCounts(all, doctorid, selectedDate, selectedSession);
  }, [fetchCounts, all, doctorid, selectedDate, selectedSession]);

  const debouncedSearchRef = useRef(debouncedPatientSearch);
  useEffect(() => {
    if (debouncedSearchRef.current === debouncedPatientSearch) return;
    debouncedSearchRef.current = debouncedPatientSearch;
    fetchAppointments(
      selectedOption,
      all,
      doctorid,
      selectedSession,
      selectedDate,
      debouncedPatientSearch,
      1
    );
  }, [
    debouncedPatientSearch,
    fetchAppointments,
    selectedOption,
    all,
    doctorid,
    selectedSession,
    selectedDate,
  ]);

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const countKey = STATUS_TO_COUNT[value] ?? value;
    if (
      ["upcoming", "in_queue", "ongoing", "complete", "no_show", "rescheduled"].includes(
        countKey
      )
    ) {
      setSelectedStatusCard(countKey);
      setSelectedSourceChip("all");
    } else if (
      ["all", "schedule", "appointment", "pre_booked", "walk_in", "call_in", "online", "cancel"].includes(
        countKey
      )
    ) {
      setSelectedSourceChip(
        countKey === "pre_booked" ? "appointment" : countKey
      );
      setSelectedStatusCard("");
    }
    fetchAppointments(
      COUNT_TO_STATUS[countKey] ?? value,
      all,
      doctorid,
      selectedSession,
      selectedDate,
      debouncedPatientSearch,
      1
    );
  };

  const handleDay = (event: React.ChangeEvent<HTMLInputElement>) => {
    const day = event.target.value;
    const nextDate = day === "all" ? selectedDate : "";
    setAll(day);
    setSelectedSession("");
    if (day !== "all") setSelectedDate("");
    fetchAppointments(
      selectedOption,
      day,
      doctorid,
      "",
      nextDate,
      debouncedPatientSearch,
      1
    );
  };

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDoctorId = event.target.value;
    setDoctorid(nextDoctorId);
    setSelectedSession("");
    fetchAppointments(
      selectedOption,
      all,
      nextDoctorId,
      "",
      selectedDate,
      debouncedPatientSearch,
      1
    );
  };

  const handleSessionChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const session = event.target.value;
    setSelectedSession(session);
    fetchAppointments(
      selectedOption,
      all,
      doctorid,
      session,
      selectedDate,
      debouncedPatientSearch,
      1
    );
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextDate = event.target.value;
    setSelectedDate(nextDate);
    setSelectedSession("");
    fetchAppointments(
      selectedOption,
      all,
      doctorid,
      "",
      nextDate,
      debouncedPatientSearch,
      1
    );
  };

  const handlePatientSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPatientSearch(event.target.value);
  };

  const refetchWithHeader = useCallback(
    (header: string) => {
      fetchAppointments(
        header,
        all,
        doctorid,
        selectedSession,
        selectedDate,
        debouncedPatientSearch,
        1
      );
    },
    [
      fetchAppointments,
      all,
      doctorid,
      selectedSession,
      selectedDate,
      debouncedPatientSearch,
    ]
  );

  const handleStatusCardSelect = useCallback(
    (statusKey: string) => {
      setSelectedStatusCard(statusKey);
      const sourceChip = selectedSourceChip || "schedule";
      const header = resolveListStatusHeader({
        statusCard: statusKey,
        sourceChip,
      });
      refetchWithHeader(header);
    },
    [refetchWithHeader, selectedSourceChip]
  );

  const handleSourceChipSelect = useCallback(
    (sourceKey: string) => {
      setSelectedSourceChip(sourceKey || "schedule");
      setSelectedStatusCard("");
      const header = resolveListStatusHeader({
        statusCard: "",
        sourceChip: sourceKey || "schedule",
      });
      refetchWithHeader(header);
    },
    [refetchWithHeader]
  );

  const handleRefresh = useCallback(() => {
    fetchCounts(all, doctorid, selectedDate, selectedSession);
    fetchAppointments(
      selectedOption,
      all,
      doctorid,
      selectedSession,
      selectedDate,
      debouncedPatientSearch,
      1
    );
  }, [
    fetchCounts,
    fetchAppointments,
    selectedOption,
    all,
    doctorid,
    selectedSession,
    selectedDate,
    debouncedPatientSearch,
  ]);

  /** Socket payloads are often partial (no reg/phone/doctor populate). Refetch full list. */
  useEffect(() => {
    const clinicId = getDecryptedToken("cid");
    let timer: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefresh = (evt?: { token?: { clinicId?: string } }) => {
      if (evt?.token?.clinicId && evt.token.clinicId !== clinicId) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        handleRefresh();
      }, 250);
    };
    socket.on("uppdateAppointment", scheduleRefresh);
    socket.on("allAppointmentStatusChange", scheduleRefresh);
    socket.on("roomAllocationUpdate", scheduleRefresh);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off("uppdateAppointment", scheduleRefresh);
      socket.off("allAppointmentStatusChange", scheduleRefresh);
      socket.off("roomAllocationUpdate", scheduleRefresh);
    };
  }, [handleRefresh]);

  const onPageChange = useCallback(
    (newPage: number) => {
      fetchAppointments(
        selectedOption,
        all,
        doctorid,
        selectedSession,
        selectedDate,
        debouncedPatientSearch,
        newPage
      );
    },
    [
      fetchAppointments,
      all,
      selectedOption,
      doctorid,
      selectedSession,
      selectedDate,
      debouncedPatientSearch,
    ]
  );

  const refreshAfterStatusChange = useCallback(() => {
    fetchCounts(all, doctorid, selectedDate, selectedSession);
    fetchAppointments(
      selectedOption,
      all,
      doctorid,
      selectedSession,
      selectedDate,
      debouncedPatientSearch,
      1
    );
  }, [
    fetchCounts,
    fetchAppointments,
    selectedOption,
    all,
    doctorid,
    selectedSession,
    selectedDate,
    debouncedPatientSearch,
  ]);

  const handleAddTreatment = useCallback(
    (patientId: string, appointmentId?: string) => {
      baseUrl
        .post("treatment/create-treatment", {
          patientId,
          ...(appointmentId ? { appointmentId } : {}),
        })
        .then((response) => {
          const qs = appointmentId
            ? `?appointmentId=${encodeURIComponent(appointmentId)}`
            : "";
          navigate(
            `/client/treatment-diagnose/add-treatment/treatment-table/${response.data.data._id}${qs}`
          );
        })
        .catch((error) => console.error(error));
    },
    [navigate]
  );

  const handleAddDiagnosis = useCallback(
    (patientId: string, appointmentId?: string) => {
      baseUrl
        .post("treatment/create-treatment", {
          patientId,
          ...(appointmentId ? { appointmentId } : {}),
        })
        .then((response) => {
          const qs = appointmentId
            ? `?appointmentId=${encodeURIComponent(appointmentId)}`
            : "";
          navigate(
            `/client/treatment-diagnose/add-treatment/diagnose/${response.data.data._id}${qs}`
          );
        })
        .catch((error) => console.error(error));
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (key: string) => {
      if (sortKey !== key) {
        setSortKey(key);
        setSortDirection("asc");
      } else {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      }
    },
    [sortKey]
  );

  const sortedAppointments = useMemo(() => {
    const rows: any[] = appointment?.allappointment ?? [];
    if (!sortKey) return rows;

    const direction = sortDirection === "asc" ? 1 : -1;
    const getSortValue = (item: any): number => {
      if (sortKey === "token") {
        return item?.queue != null && item?.queue !== ""
          ? Number(item.queue)
          : -Infinity;
      }
      if (sortKey === "datetime") {
        const datePart = item?.date ? String(item.date).split("T")[0] : "";
        const timePart = item?.startTime ?? "";
        const parsed = new Date(`${datePart}T${timePart}`).getTime();
        return Number.isFinite(parsed) ? parsed : -Infinity;
      }
      return 0;
    };

    return [...rows].sort(
      (a, b) => (getSortValue(a) - getSortValue(b)) * direction
    );
  }, [appointment?.allappointment, sortKey, sortDirection]);

  const doctorOptions = useMemo<DoctorOption[]>(
    () =>
      (doctor?.doctors ?? [])
        .filter(
          (item: {
            IsDentist?: boolean;
            _id?: string;
            title?: string;
            firstName?: string;
            lastName?: string;
          }) => Boolean(item?.IsDentist)
        )
        .map(
          (item: {
            _id?: string;
            title?: string;
            firstName?: string;
            lastName?: string;
          }) => {
            const fullName = [item.title, item.firstName, item.lastName]
              .filter(Boolean)
              .join(" ")
              .trim();
            return {
              id: String(item._id),
              label: fullName || "Doctor",
            };
          }
        ),
    [doctor?.doctors]
  );

  const activeStatusLabel =
    FILTER_LABELS[selectedStatusCard] ||
    FILTER_LABELS[selectedSourceChip] ||
    FILTER_LABELS[selectedOption] ||
    "Schedule";

  const hasExtraFilters =
    Boolean(doctorid) ||
    Boolean(selectedSession) ||
    Boolean(debouncedPatientSearch) ||
    (all === "all" && Boolean(selectedDate));

  const emptyState = (
    <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
      <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-700/80 flex items-center justify-center mb-4">
        <SearchOffIcon
          className="text-slate-400 dark:text-slate-500"
          sx={{ fontSize: 32 }}
        />
      </div>
      <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1">
        No appointments found
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
        {debouncedPatientSearch
          ? `No results for "${debouncedPatientSearch}". Try a different name or clear your search.`
          : `No ${activeStatusLabel.toLowerCase()} appointments match your current filters.`}
      </p>
      {hasExtraFilters ? (
        <button
          type="button"
          onClick={() => {
            setDoctorid("");
            setSelectedSession("");
            setSelectedDate("");
            setPatientSearch("");
            setDebouncedPatientSearch("");
            fetchAppointments(selectedOption, all, "", "", "", "", 1);
          }}
          className="mt-4 px-4 py-2 rounded-lg text-sm font-medium text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
        >
          Clear all filters
        </button>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="bg-white dark:bg-slate-800 rounded-xl md:rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3 md:p-5">
        <AppointmentFilter
          variant="toolbar"
          hideStatusFilter
          selectedOption={selectedOption}
          all={all}
          handleOptionChange={handleOptionChange}
          handleDay={handleDay}
          sessions={sessions}
          selectedSession={selectedSession}
          handleSessionChange={handleSessionChange}
          showSessionFilter
          selectedDate={selectedDate}
          handleDateChange={handleDateChange}
          doctors={doctorOptions}
          selectedDoctorId={doctorid}
          onDoctorChange={handleChange}
          patientSearch={patientSearch}
          onPatientSearchChange={handlePatientSearchChange}
        />

        <div className="mt-4 mb-3">
          <AppointmentCounts
            count={appointment?.allappointmentCount}
            compact
            activeStatus={selectedStatusCard}
            activeSource={selectedSourceChip}
            onStatusSelect={handleStatusCardSelect}
            onSourceSelect={handleSourceChipSelect}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 mb-3 px-0.5">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <span className="font-semibold text-slate-800 dark:text-slate-100 tabular-nums">
              {isLoading ? "…" : counts}
            </span>{" "}
            {counts === 1 ? "result" : "results"}
            <span className="text-slate-400 dark:text-slate-500 mx-1.5">·</span>
            {activeStatusLabel}
            {all === "today" ? (
              <span className="text-slate-400 dark:text-slate-500"> · Today</span>
            ) : all === "tomorrow" ? (
              <span className="text-slate-400 dark:text-slate-500"> · Tomorrow</span>
            ) : selectedDate ? (
              <span className="text-slate-400 dark:text-slate-500"> · {selectedDate}</span>
            ) : (
              <span className="text-slate-400 dark:text-slate-500"> · All dates</span>
            )}
            {debouncedPatientSearch ? (
              <span className="text-slate-400 dark:text-slate-500">
                {" "}
                · &quot;{debouncedPatientSearch}&quot;
              </span>
            ) : null}
          </p>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-700/80 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600 disabled:opacity-60 transition-all"
            title="Refresh appointments"
          >
            <RefreshIcon
              sx={{ fontSize: 16 }}
              className={isLoading ? "animate-spin text-indigo-500" : ""}
            />
            Refresh
          </button>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block relative">
          <div className="overflow-auto max-h-[min(68vh,720px)] rounded-xl border border-slate-200 dark:border-slate-600">
            <table className="w-full">
              <AppointmentTableHead
                sticky
                sortableKeys={["datetime", "token"]}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSortChange={handleSortChange}
              />
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700/80 [&>tr:nth-child(even)]:bg-slate-50/60 dark:[&>tr:nth-child(even)]:bg-slate-800/50">
                {sortedAppointments.map((item: any, index: number) => (
                  <AppointmentTr
                    appointmStatus={refreshAfterStatusChange}
                    key={item?._id ?? index}
                    item={item}
                    showPatientDetailsButton
                  />
                ))}
              </tbody>
            </table>
            {!isLoading && appointment?.allappointment?.length === 0 ? emptyState : null}
          </div>
          {isLoading && (appointment?.allappointment?.length ?? 0) === 0 ? (
            <TableSkeleton />
          ) : null}
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {isLoading && (appointment?.allappointment?.length ?? 0) === 0 ? (
            <MobileSkeleton />
          ) : null}
          {appointment?.allappointment?.map((item: any, index: number) => (
            <AppointmentMobileCard
              key={item?._id ?? index}
              item={item}
              onOpenDetails={() => setDetailsItem(item)}
              onStatusChange={refreshAfterStatusChange}
              showPatientDetailsButton
              showAddTreatmentButton={false}
              onAddTreatment={handleAddTreatment}
              onAddDiagnosis={handleAddDiagnosis}
            />
          ))}
          {!isLoading && appointment?.allappointment?.length === 0 ? emptyState : null}
        </div>

        {all !== "today" ? (
          <BottomTab
            key={`${all}-${selectedOption}-${doctorid}-${selectedSession}-${selectedDate}-${debouncedPatientSearch}`}
            title="Patient"
            count={counts}
            onPageChange={onPageChange}
          />
        ) : null}
      </div>

      <AppointmentDetailsModal
        item={detailsItem ?? {}}
        open={detailsItem != null}
        onClose={() => setDetailsItem(null)}
        onStatusChange={refreshAfterStatusChange}
      />
    </div>
  );
};

function TableSkeleton() {
  return (
    <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/60 backdrop-blur-[1px] flex flex-col gap-0">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-14 border-b border-slate-100 dark:border-slate-700/80 animate-pulse bg-gradient-to-r from-slate-100 via-slate-50 to-slate-100 dark:from-slate-700 dark:via-slate-800 dark:to-slate-700"
          style={{ animationDelay: `${i * 80}ms` }}
        />
      ))}
    </div>
  );
}

function MobileSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-36 rounded-xl border border-slate-200 dark:border-slate-600 animate-pulse bg-slate-100 dark:bg-slate-700/50"
        />
      ))}
    </>
  );
}

export default AllAppointment;
